import type { AxiosError } from "axios";

import apiClient from "@/utils/api-client";
import { del, fetchData, patch, post, sendBackendForm } from "@/utils/api-handler";
import type {
  Paginated,
  ShareCandidate,
  SkillDetail,
  SkillFileContent,
  SkillListItem,
  SkillScope,
  SkillShare,
  SkillVisibility,
} from "@/types/command-skill";

const BASE = "/api/command-team/skills";

export interface SkillApiError {
  status?: number;
  code?: string;
  message: string;
}

export function readSkillError(error: unknown, fallback = "Something went wrong"): SkillApiError {
  const axiosError = error as AxiosError<{
    error_code?: string;
    message?: string;
    detail?: unknown;
  }>;
  const status = axiosError.response?.status;
  const data = axiosError.response?.data;
  if (data && typeof data === "object") {
    if (typeof data.message === "string" && data.message.trim()) {
      return { status, code: data.error_code, message: data.message };
    }
    if (Array.isArray(data.detail)) {
      const parts = data.detail
        .map((item) => {
          if (item && typeof item === "object" && "msg" in item && typeof item.msg === "string") {
            return item.msg;
          }
          return null;
        })
        .filter((item): item is string => Boolean(item));
      if (parts.length > 0) return { status, message: parts.join(" ") };
    }
    if (typeof data.detail === "string" && data.detail.trim()) {
      return { status, message: data.detail };
    }
  }
  return { status, message: fallback };
}

export function isSkillMissing(error: unknown): boolean {
  return readSkillError(error).code === "COMMAND_TEAM__SKILL_NOT_FOUND";
}

export function listSkills(query: {
  section?: SkillScope;
  search?: string;
  page?: number;
  limit?: number;
}): Promise<Paginated<SkillListItem>> {
  const params = new URLSearchParams();
  params.set("page", String(query.page ?? 1));
  params.set("limit", String(query.limit ?? 50));
  params.set("sort_by", "name");
  params.set("sort_order", "asc");
  if (query.section) params.set("section", query.section);
  if (query.search) params.set("search", query.search);
  return fetchData(`${BASE}?${params.toString()}`);
}

export function getSkill(id: string): Promise<SkillDetail> {
  return fetchData(`${BASE}/${id}`);
}

export function getSkillFile(id: string, path: string): Promise<SkillFileContent> {
  const params = new URLSearchParams({ path });
  return fetchData(`${BASE}/${id}/files?${params.toString()}`);
}

export function listShareCandidates(query: {
  search?: string;
  page?: number;
  limit?: number;
}): Promise<Paginated<ShareCandidate>> {
  const params = new URLSearchParams();
  params.set("page", String(query.page ?? 1));
  params.set("limit", String(query.limit ?? 20));
  if (query.search) params.set("search", query.search);
  return fetchData(`${BASE}/share-candidates?${params.toString()}`);
}

export async function createSkill(body: {
  name: string;
  description: string;
  instructions: string;
}): Promise<{ id: string }> {
  const response = await post(BASE, body);
  return response.data as { id: string };
}

export async function uploadSkillPackage(
  file: File,
  onProgress?: (percent: number) => void,
): Promise<{ id: string }> {
  const form = new FormData();
  form.append("file", file);
  // Straight to the backend, like Command file uploads: the /api proxy rejects bodies over ~4.5 MB
  const response = await sendBackendForm<{ id: string }>("post", `${BASE}/upload`, form, onProgress);
  return response.data;
}

export async function updateSkill(
  id: string,
  body: { description?: string; instructions?: string },
): Promise<SkillDetail> {
  const response = await patch(`${BASE}/${id}`, body);
  return response.data as SkillDetail;
}

export async function uploadSkillVersion(
  id: string,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<SkillDetail> {
  const form = new FormData();
  form.append("file", file);
  const response = await sendBackendForm<SkillDetail>("patch", `${BASE}/${id}/archive`, form, onProgress);
  return response.data;
}

export async function uploadSkillPath(
  id: string,
  path: string,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<SkillDetail> {
  const form = new FormData();
  form.append("path", path);
  form.append("file", file);
  const response = await sendBackendForm<SkillDetail>("patch", `${BASE}/${id}/files`, form, onProgress);
  return response.data;
}

export async function deleteSkillFile(id: string, path: string): Promise<SkillDetail> {
  const params = new URLSearchParams({ path });
  const response = await del(`${BASE}/${id}/files?${params.toString()}`);
  return response.data as SkillDetail;
}

export async function updateSkillVisibility(
  id: string,
  visibility: SkillVisibility,
): Promise<SkillDetail> {
  const response = await patch(`${BASE}/${id}/visibility`, { visibility });
  return response.data as SkillDetail;
}

export async function updateSkillShares(
  id: string,
  userIds: string[],
): Promise<{ skill_id: string; shares: SkillShare[] }> {
  const response = await patch(`${BASE}/${id}/shares`, { user_ids: userIds });
  return response.data as { skill_id: string; shares: SkillShare[] };
}

export async function toggleSkill(
  id: string,
  enabled: boolean,
): Promise<{ skill_id: string; enabled: boolean }> {
  const response = await patch(`${BASE}/${id}/toggle`, { enabled });
  return response.data as { skill_id: string; enabled: boolean };
}

export async function deleteSkill(id: string): Promise<{ deleted: string }> {
  const response = await del(`${BASE}/${id}`);
  return response.data as { deleted: string };
}

export async function downloadSkillArchive(id: string, fallbackName: string): Promise<void> {
  const response = await apiClient.get<Blob>(`${BASE}/${id}/download`, { responseType: "blob" });
  const header = String(response.headers["content-disposition"] ?? "");
  const match = header.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
  const filename = match?.[1]
    ? decodeURIComponent(match[1].replace(/["']/g, "").trim())
    : `${fallbackName}.zip`;
  const url = URL.createObjectURL(response.data);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
